'use client'

import { layoutWithELK } from '@/components/Flowchart/layoutUtils'
import { useCopilotContext } from '@/lib/features/copilotv2/CopilotContext'
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import ReactFlow, {
  Edge,
  Node,
  PanOnScrollMode,
  useEdgesState,
  useNodesState,
  useReactFlow,
  Viewport,
} from 'reactflow'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { WorkflowConfig } from 'sprites-types/workflow/core'
import { EdgeInfoModal } from './EdgeInfoModal'
import { CustomEdge } from './EdgeLabel'
import { FlowchartControls } from './FlowchartControls'
import { FlowchartNode } from './FlowchartNode'
import FlowchartScrollbars from './FlowchartScrollbars'

const FLOWCHART_PAN_EXTENTS = 110
const FLOWCHART_ZOOM_DURATION = 500
const FLOWCHART_NODE_WIDTH = 320 // Matches actual rendered width from layoutUtils.ts

interface FlowchartProps {
  flowchartData: WorkflowConfig
  selectedNodes: string[]
  onNodeClick: (event: React.MouseEvent, node: Node) => void
  onPaneClick: () => void
  debugToggle?: boolean
  onOpenPanel?: (content: any, breadcrumbs: any[]) => void
  isWorkflowExecuting?: boolean // Hide retry buttons if workflow is currently executing
  workflowVersion?: number // Workflow version for display purposes
  nodeExecutions?: Record<string, NodeExecution>
  workflowExecutionId?: string
}

export function FlowchartComponent({
  flowchartData,
  selectedNodes,
  onNodeClick,
  onPaneClick,
  onOpenPanel,
  isWorkflowExecuting = false,
  workflowVersion,
  nodeExecutions,
  workflowExecutionId,
}: FlowchartProps) {
  const [nodes, setNodes, onNodesChange] = useNodesState<Node[]>([])
  const [rawEdges, setRawEdges, onEdgesChange] = useEdgesState<Edge[]>([])

  const edges = useMemo(() => {
    const seenIds = new Set<string>()
    return rawEdges.filter((edge) => {
      if (seenIds.has(edge.id)) {
        return false
      }
      seenIds.add(edge.id)
      return true
    })
  }, [rawEdges])
  const { setViewport, getViewport } = useReactFlow()
  const [isLoading, setIsLoading] = useState(true)
  const [isInitialFitComplete, setIsInitialFitComplete] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const flowchartDataRef = useRef(flowchartData)
  const selectedNodesRef = useRef<string[]>([])
  const fitHorizontallyRef = useRef<() => void>(() => {})
  const nodeRefs = useRef<Record<string, HTMLDivElement | null>>({})
  const [measuredHeights, setMeasuredHeights] = useState<Record<string, number> | undefined>(
    undefined
  )
  const [executingNodeId, setExecutingNodeId] = useState<string | null>(null)

  // Modal state for edge info
  const [edgeInfoModal, setEdgeInfoModal] = useState<{
    isOpen: boolean
    edgeData?: {
      tag: string
      description?: string
      condition?: string
    }
  }>({
    isOpen: false,
  })

  // Get copilot context to track node execution changes
  const { context } = useCopilotContext()

  const MAX_ZOOM = 2
  const [minZoom, setMinZoom] = useState(0.5)
  const [panExtent, setPanExtent] = useState<[[number, number], [number, number]] | undefined>(
    undefined
  )

  // Track zoom state
  const [defaultZoom, setDefaultZoom] = useState<number>(1.5)

  // Handler for opening edge info modal
  const handleEdgeInfoClick = useCallback(
    (edgeData: { tag: string; description?: string; condition?: string }) => {
      setEdgeInfoModal({
        isOpen: true,
        edgeData,
      })
    },
    []
  )

  // Handler for closing edge info modal
  const handleEdgeInfoClose = useCallback(() => {
    setEdgeInfoModal({
      isOpen: false,
    })
  }, [])

  // Memoize nodeTypes to prevent React Flow warnings
  const nodeTypes = useMemo(
    () => ({
      flowchartNode: (props: any) => (
        <div
          ref={(el) => {
            nodeRefs.current[props.id] = el
          }}
        >
          <FlowchartNode
            {...props}
            onOpenPanel={onOpenPanel}
            isWorkflowExecuting={isWorkflowExecuting}
            workflowConfig={flowchartData}
            workflowVersion={workflowVersion}
            nodeExecutions={nodeExecutions}
            workflowExecutionId={workflowExecutionId}
          />
        </div>
      ),
    }),
    [
      onOpenPanel,
      isWorkflowExecuting,
      flowchartData,
      workflowVersion,
      nodeExecutions,
      workflowExecutionId,
    ]
  )

  // Memoize edgeTypes to prevent React Flow warnings
  const edgeTypes = useMemo(
    () => ({
      custom: CustomEdge,
    }),
    []
  )

  // Cache scroll limits for the last used zoom
  const scrollLimits = useRef<{
    minY: number
    maxY: number
    minX: number
    maxX: number
  } | null>(null)

  // Update refs when props change
  useEffect(() => {
    flowchartDataRef.current = flowchartData
  }, [flowchartData])

  // Update selected nodes ref
  useEffect(() => {
    selectedNodesRef.current = selectedNodes
  }, [selectedNodes])

  // Update node selection when selectedNodes changes
  useEffect(() => {
    setNodes((currentNodes) =>
      currentNodes.map((node) => ({
        ...node,
        selected: selectedNodes.includes(node.id),
      }))
    )
  }, [selectedNodes])

  // After nodes are rendered, measure their heights
  useEffect(() => {
    if (!nodes.length) {
      return
    }

    let rafId2: number | undefined

    // Use multiple RAF to ensure layout is complete
    const rafId1 = requestAnimationFrame(() => {
      rafId2 = requestAnimationFrame(() => {
        const heights: Record<string, number> = {}
        let hasNewOrChangedHeights = false

        nodes.forEach((node) => {
          const ref = nodeRefs.current[node.id]
          if (ref) {
            const height = ref.offsetHeight
            heights[node.id] = height

            // Check if this is a new measurement or changed measurement
            if (!measuredHeights || measuredHeights[node.id] !== height) {
              hasNewOrChangedHeights = true
            }
          }
        })

        // Only update if we have valid heights AND something actually changed
        const hasMeasuredHeights = Object.keys(heights).length > 0
        const allHeightsValid = Object.values(heights).every((h) => h > 0)

        if (hasMeasuredHeights && allHeightsValid && hasNewOrChangedHeights) {
          setMeasuredHeights((prev) => {
            // Double-check that we're actually changing something
            const merged = { ...prev, ...heights }
            const isActuallyDifferent =
              !prev ||
              Object.keys(merged).length !== Object.keys(prev).length ||
              Object.entries(heights).some(([id, h]) => prev[id] !== h)

            return isActuallyDifferent ? merged : prev
          })
        }
      })
    })

    return () => {
      cancelAnimationFrame(rafId1)
      if (rafId2 !== undefined) {
        cancelAnimationFrame(rafId2)
      }
    }
  }, [nodes, measuredHeights, context.nodeExecutions])

  // Refit when details visibility changes
  useEffect(() => {
    if (!isLoading && nodes.length > 0 && containerRef.current) {
      // Calculate graph bounds
      const graphTop = Math.min(...nodes.map((n) => n.position.y)) - FLOWCHART_PAN_EXTENTS
      const graphBottom =
        Math.max(...nodes.map((n) => n.position.y + (n.height || 100))) + FLOWCHART_PAN_EXTENTS
      const graphLeft = Math.min(...nodes.map((n) => n.position.x)) - FLOWCHART_PAN_EXTENTS
      const graphRight =
        Math.max(...nodes.map((n) => n.position.x + FLOWCHART_NODE_WIDTH)) + FLOWCHART_PAN_EXTENTS

      // Set pan extent
      setPanExtent([
        [graphLeft, graphTop],
        [graphRight, graphBottom],
      ])

      // Refit horizontally when details panel toggles
      setTimeout(() => {
        fitHorizontallyRef.current?.()
      }, 10)
    }
  }, [isLoading, nodes])

  // Calculate horizontal fit based on nodes
  const fitHorizontally = useCallback(() => {
    if (!nodes.length || !containerRef.current) return

    const containerWidth = containerRef.current.clientWidth
    const containerHeight = containerRef.current.clientHeight
    const padding = 40

    // Calculate node bounds with extra padding for edge labels
    const edgeLabelPadding = 40 // Extra padding to account for edge labels
    const bounds = nodes.reduce(
      (acc, node) => {
        const left = node.position.x - edgeLabelPadding
        const right = node.position.x + FLOWCHART_NODE_WIDTH + edgeLabelPadding
        const top = node.position.y
        const bottom = node.position.y + (node.height || 100)

        return {
          left: Math.min(acc.left, left),
          right: Math.max(acc.right, right),
          top: Math.min(acc.top, top),
          bottom: Math.max(acc.bottom, bottom),
        }
      },
      { left: Infinity, right: -Infinity, top: Infinity, bottom: -Infinity }
    )

    // Calculate total dimensions needed
    const graphWidth = bounds.right - bounds.left
    const graphHeight = bounds.bottom - bounds.top
    const effectiveContainerWidth = containerWidth - padding * 2
    const effectiveContainerHeight = containerHeight - padding * 2

    // Calculate minimum zoom to fit entire graph (both width and height)
    const zoomToFitWidth = effectiveContainerWidth / graphWidth
    const zoomToFitHeight = effectiveContainerHeight / graphHeight
    const calculatedMinZoom = Math.min(zoomToFitWidth, zoomToFitHeight)

    // Update min zoom dynamically based on graph size, but allow zooming out further
    // Use a lower minimum to give users more flexibility to zoom out beyond the default view
    setMinZoom(Math.max(0.1, calculatedMinZoom * 0.5))

    // Calculate zoom to show approximately 4 nodes in height by default
    // Estimate height for 4 nodes: ~150px per node + 90px spacing per layer = ~240px per layer
    const targetHeightFor4Nodes = 240 * 4 // 960px for 4 layers
    const zoomFor4Nodes = effectiveContainerHeight / targetHeightFor4Nodes

    // Use the zoom that shows about 4 nodes in height, but don't go smaller than min zoom
    // and don't go larger than what fits the width
    const horizontalZoom = Math.min(
      MAX_ZOOM,
      Math.max(calculatedMinZoom, Math.min(zoomFor4Nodes, zoomToFitWidth))
    )

    // Center horizontally
    const xPos =
      -bounds.left * horizontalZoom + (effectiveContainerWidth - graphWidth * horizontalZoom) / 2

    // Center vertically on the graph
    const yPos =
      -bounds.top * horizontalZoom + (effectiveContainerHeight - graphHeight * horizontalZoom) / 2

    // Create new viewport with centered position
    const newViewport: Viewport = {
      x: xPos,
      y: yPos,
      zoom: horizontalZoom,
    }
    setViewport(newViewport, { duration: FLOWCHART_ZOOM_DURATION })

    // Update default zoom
    setDefaultZoom(horizontalZoom)

    // Update scroll limits cache
    const graphTop = Math.min(...nodes.map((n) => n.position.y)) - 80
    const graphBottom = Math.max(...nodes.map((n) => n.position.y + (n.height || 100))) + 80
    const graphLeft = Math.min(...nodes.map((n) => n.position.x)) - 80
    const graphRight = Math.max(...nodes.map((n) => n.position.x + FLOWCHART_NODE_WIDTH)) + 80

    scrollLimits.current = {
      minY: graphTop,
      maxY: graphBottom,
      minX: graphLeft,
      maxX: graphRight,
    }

    // Set pan extent for React Flow
    setPanExtent([
      [scrollLimits.current.minX, scrollLimits.current.minY],
      [scrollLimits.current.maxX, scrollLimits.current.maxY],
    ])

    // Mark initial fit as complete
    setIsInitialFitComplete(true)
  }, [nodes, edges, getViewport, setViewport, defaultZoom])

  // Store the latest version of fitHorizontally in ref
  useLayoutEffect(() => {
    fitHorizontallyRef.current = fitHorizontally
    return () => {
      fitHorizontallyRef.current = () => {}
    }
  }, [fitHorizontally])

  // Initial layout effect (now supports measured heights)
  useEffect(() => {
    let mounted = true

    const performLayout = async () => {
      setIsLoading(true)
      setIsInitialFitComplete(false)
      try {
        const { nodes: layoutedNodes, edges: layoutedEdges } = await layoutWithELK(
          flowchartDataRef.current,
          measuredHeights
        )

        if (!mounted) {
          return
        }

        // Add onInfoClick handler to edges
        const edgesWithHandlers = layoutedEdges.map((edge) => ({
          ...edge,
          data: edge.data
            ? {
                ...edge.data,
                onInfoClick: edge.data.condition ? () => handleEdgeInfoClick(edge.data) : undefined,
              }
            : undefined,
        }))

        // Show all nodes immediately without animation
        setNodes(layoutedNodes)
        setRawEdges(edgesWithHandlers)

        setTimeout(() => fitHorizontallyRef.current?.(), 10)
      } catch (error) {
        console.error('Layout failed:', error)
        console.warn(
          'Workflow may have structural issues (invalid edges, missing nodes, etc.). Layout will continue with fallback.'
        )
        // Layout function now handles errors gracefully with fallback layout
      } finally {
        if (mounted) {
          setIsLoading(false)
        }
      }
    }

    performLayout()

    return () => {
      mounted = false
    }
  }, [flowchartData, measuredHeights, context.nodeExecutions])

  // Track executing node and center viewport on it
  useEffect(() => {
    if (!context.nodeExecutions || !nodes.length || !containerRef.current) {
      return
    }

    // Find the currently executing node
    const newExecutingNodeId =
      Object.keys(context.nodeExecutions).find((nodeId) => {
        const status = context.nodeExecutions?.[nodeId]?.status
        return status === 'running' || status === 'pending'
      }) || null

    // Only update if the executing node changed
    if (newExecutingNodeId !== executingNodeId) {
      setExecutingNodeId(newExecutingNodeId)

      // Don't do anything if there's no executing node
      if (!newExecutingNodeId) {
        return
      }

      // Find the node in the nodes array
      const executingNode = nodes.find((n) => n.id === newExecutingNodeId)
      if (!executingNode) {
        return
      }

      // Center viewport on the executing node
      const containerWidth = containerRef.current.clientWidth
      const containerHeight = containerRef.current.clientHeight
      const currentViewport = getViewport()

      // Calculate position to center the node
      const nodeWidth = executingNode.width || FLOWCHART_NODE_WIDTH
      const nodeHeight = executingNode.height || 100

      const x =
        -executingNode.position.x * currentViewport.zoom +
        (containerWidth - nodeWidth * currentViewport.zoom) / 2
      const y =
        -executingNode.position.y * currentViewport.zoom +
        (containerHeight - nodeHeight * currentViewport.zoom) / 2

      setViewport(
        {
          x,
          y,
          zoom: currentViewport.zoom,
        },
        { duration: FLOWCHART_ZOOM_DURATION }
      )
    }
  }, [context.nodeExecutions, nodes, executingNodeId, setViewport, getViewport])

  // Wrapper for onNodeClick that prevents selection of visual start/end nodes
  const handleNodeClick = useCallback(
    (event: React.MouseEvent, node: Node) => {
      // Prevent selection of visual start and end nodes
      if (node.id === '__start__' || node.id === '__end__') {
        return
      }
      onNodeClick(event, node)
    },
    [onNodeClick]
  )

  const onConnect = useCallback(() => {
    // Do nothing - read-only
  }, [])

  useEffect(() => {
    let timeout: any

    function trySetup(attempt = 1) {
      if (!containerRef.current) {
        console.log(`trySetup attempt ${attempt}`)
        if (attempt < 20) {
          console.log(`setting timeout to ${50 * Math.pow(2, attempt - 1)}`)
          timeout = setTimeout(() => trySetup(attempt + 1), 50 * Math.pow(2, attempt - 1))
        }
        return
      }

      const handleResize = () => {
        fitHorizontallyRef.current?.()
      }

      const resizeObserver = new window.ResizeObserver(handleResize)
      resizeObserver.observe(containerRef.current)

      window.addEventListener('resize', handleResize)

      // Clean up
      return () => {
        clearTimeout(timeout)
        resizeObserver.disconnect()
        window.removeEventListener('resize', handleResize)
      }
    }

    return trySetup()
  }, [])

  return (
    <div ref={containerRef} className="relative h-full w-full" style={{ overflow: 'visible' }}>
      <div
        className={`h-full w-full transition-opacity duration-300 ${
          isLoading || !isInitialFitComplete ? 'opacity-0' : 'opacity-100'
        }`}
      >
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          onNodeClick={handleNodeClick}
          onPaneClick={onPaneClick}
          panOnDrag={true}
          panOnScroll
          panOnScrollMode={PanOnScrollMode.Free}
          zoomOnPinch={true}
          zoomOnScroll={true}
          zoomOnDoubleClick={false}
          minZoom={minZoom}
          maxZoom={MAX_ZOOM}
          defaultViewport={{ x: 0, y: 0, zoom: 1 }}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={true}
          edgesUpdatable={false}
          edgesFocusable={false}
          translateExtent={panExtent}
          proOptions={{ hideAttribution: true }}
          fitView={false}
          preventScrolling={false}
        >
          <FlowchartControls onReset={fitHorizontally} />
        </ReactFlow>
        <FlowchartScrollbars panExtent={panExtent} containerRef={containerRef} />
      </div>

      {/* Edge Info Modal */}
      <EdgeInfoModal
        isOpen={edgeInfoModal.isOpen}
        onClose={handleEdgeInfoClose}
        edgeData={edgeInfoModal.edgeData}
      />
    </div>
  )
}
